import React, { useRef, useState } from "react";
import { Grid, Typography, Box, Backdrop, TextField } from "@material-ui/core";
import moment from "moment";
import { useFormik } from "formik";
import * as Yup from "yup";

import CommonButton from "src/components/CommonButton/CommonButton";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CommonLoader from "src/components/Loader/CommonLoader";
import { useStyles } from "../style";

export function AuctionReport() {
  const classes = useStyles();
  const [loader, setLoader] = useState<boolean>(false);

  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().nullable(), // optional
    endDate: Yup.string()
      .required("End date is required")
      .test("is-after-start", "End date must be after start date", function (value) {
        const { startDate } = this.parent;
        if (!startDate) return true; // skip test if startDate is not provided
        return moment(value).isSameOrAfter(moment(startDate));
      }),
    noOfDays: Yup.number()
      .nullable()
      .typeError("Must be a number")
      .min(0, "Number of days must be 0 or more"),
  });

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: moment().format("YYYY-MM-DDTHH:mm"),
      noOfDays: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoader(true);
      try {
        const idToken = localStorage.getItem("token");

        // Only include startDate if it's filled
        const requestData: any = {
          endDate: values.endDate,
        };

        if (values.startDate && values.startDate.trim() !== "") {
          requestData.startDate = values.startDate;
        }
        if (values.noOfDays) {
          requestData.noOfDays = Number(values.noOfDays);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get-container-auction-report`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestData),
          }
        );

        if (response.status !== 200) throw new Error("Failed to download report");

        const blob = await response.blob();
        const file = new File(
          [blob],
          `Auction-Report_${moment().format("DD-MMM-YY_HH-mm-A")}.xlsx`
        );
        const link = document.createElement("a");
        link.href = URL.createObjectURL(file);
        link.download = file.name;
        link.click();
      } catch (error) {
        console.error("Error downloading report:", error);
        // Optional: add user notification
      } finally {
        setLoader(false);
      }
    },
  });
  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Grid container direction="row" justifyContent="space-between" style={{ paddingTop: "10px" }}>
        <Typography className={classes.pageSubHeading}>Auction Report</Typography>
        <Grid item xs={12} className={classes.outer} container direction="row">
          <Grid item xs={12} md={4}>
            {/* <Box>
              <CommonTextFieldDate
                label={"Start Date"}
                name={"startDate"}
                cssClassName="customTextField"
                value={formik.values.startDate}
                datePickerRef={startDatePickerRef}
                onChange={(e: any) => {
                  formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  formik.setFieldValue("endDate", "");
                  formik.setFieldTouched("endDate", true);
                }}
                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                helperText={formik.touched.startDate && formik.errors.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Box> */}
            <Box sx={{ position: "relative" }}>
              <CommonTextFieldDate
                label="Start Date"
                name="startDate"
                cssClassName="customTextField"
                value={formik.values.startDate}
                datePickerRef={startDatePickerRef}
                onChange={(e: any) => {
                  formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  formik.setFieldValue("endDate", "");
                  formik.setFieldTouched("endDate", true);
                }}
                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                helperText={formik.touched.startDate && formik.errors.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
              {formik.values.startDate && (
                <Box
                  onClick={() => formik.setFieldValue("startDate", "")}
                  style={{
                    position: "absolute",
                    top: "15%",
                    right: 10,
                    cursor: "pointer",
                    zIndex: 1,
                    color: "#737171ff",
                    fontSize: "15px",
                    fontWeight: "bold",
                  }}
                >
                  &#10005; {/* Unicode cross (×) */}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                marginLeft: { xs: 0, md: "10px" },
                marginTop: { xs: "10px", md: 0 },
              }}
            >
              <CommonTextFieldDate
                label={"End Date"}
                name={"endDate"}
                cssClassName="customTextField"
                value={formik.values.endDate}
                datePickerRef={endDatePickerRef}
                onChange={(e: any) => {
                  formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                }}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={formik.touched.endDate && formik.errors.endDate}
                minDate={formik.values.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                required
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                marginLeft: { xs: 0, md: "10px" },
                marginTop: { xs: "10px", md: 0 },
              }}
            >
              <TextField
                className="customTextField"
                type="number"
                required={true} // make it required if needed
                size="small"
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Pending Since (Days)"
                name="noOfDays"
                placeholder="Enter number of days"
                value={formik.values.noOfDays}
                onChange={formik.handleChange}
                error={formik.touched.noOfDays && Boolean(formik.errors.noOfDays)}
                helperText={formik.touched.noOfDays && formik.errors.noOfDays}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box
              sx={{
                marginLeft: { xs: 0, md: "10px" },
                marginTop: { xs: "10px", md: 0 },
              }}
            >
              <CommonButton
                isGreen
                cssClass={classes.checkButton}
                onClick={() => formik.submitForm()}
              >
                Download Report
              </CommonButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
